import { DialogWithBackdropClick } from "../DialogWithBackdropClick/DialogWithBackdropClick";
import {
  DialogActions,
  DialogContent,
  DialogProps,
  Button,
  Grid,
  FormControl,
  Box,
} from "@mui/material";
import { StyledDialogTitle } from "../StyledDialogTitle/StyledDialogTitle";
import { useAppGlobalState, useAppSiteState, useVrsTranslationState } from "../../context/AppContext/AppContext";
import { styled } from "@mui/material/styles";
import SiteSelectionComponent from "../SiteSelectionComponent/SiteSelectionComponent";
import { PlantSearch } from "../PlantSearchComponent/PlantSearch/PlantSearch";
import { useCallback } from "react";
import { useConfigActions } from "../../actions/configActions";
import { useCompanyActions } from "../../actions/companyActions";
import { getCurrentUser } from "../../libs/authLib";
import { useSiteActions } from "../../actions/siteActions";
import UserHelper from "../../helpers/UserHelper";

export const RootContainer = styled(Grid)({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
});

export const ActionButtonsContainer = styled(DialogActions)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
});

interface SiteSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  maxWidth?: DialogProps["maxWidth"];
}

const FormControlBox = styled(FormControl)({
  display: 'flex',
  width: '100%',
  flexFlow: 'column',
  alignItems: 'flex-end',
  padding: '0px 10px'
});

const SiteSelectionDialog = ({ open, onClose, maxWidth }: SiteSelectionDialogProps) => {
  const { _T } = useVrsTranslationState();
  const configActions = useConfigActions();
  const companyActions = useCompanyActions();
  const siteState = useAppSiteState();
  const siteActions = useSiteActions();
  const { sites } = siteState;
  const { appFilters } = useAppGlobalState();

  const canAccessVrsWithField = (field) => !!UserHelper.CanWithField('vrs', 'authModule', field);

  const isExternalUser =
    canAccessVrsWithField('Administrator') || canAccessVrsWithField('Maintenance') || canAccessVrsWithField('User');


  const setPlantSearchTerm = useCallback(
    (plantSearchTerm) => {
      configActions.setBarFilter("commonFilter", (s) => ({ ...s, plantSearchTerm }));
    },
    [configActions.setBarFilter]
  );

  const onCloseHandler = useCallback(() => {
    configActions.setBarFilter("commonFilter", (s) => ({
      ...s,
      plantSearchTerm: "",
    }));
    onClose();
  }, [configActions, onClose]);

  const performSiteChange = useCallback(
    (siteId) => {
      (async () => {
        const currentUser: any = await getCurrentUser();
        if (currentUser && siteId) {
          if (!sites.find((el) => el.id === siteId)) {
            const userSite = await siteActions.loadSingleSite(siteId);
            const vrsSite = await companyActions.getPlantAppSync(siteId);
            if (userSite) {
              configActions.setAppDataInitialization("firstSiteData", (state) => ({
                ...state,
                loaded: true,
                isExternal: vrsSite?.isExternal,
              }));
            }
          }
          await siteActions.setSite(siteId, currentUser);
        }
      })();
    },
    [configActions, siteActions, companyActions, sites]
  );

  const plantSearchPlantSelectionChangedHandler = useCallback(
    (plant: any) => {
      const activeCompanyId = plant?.companyId;
      const activePlantId = plant?.plantId || '';

      companyActions.setVrsCompanyAndSiteId(activeCompanyId, activePlantId);

      configActions.setBarFilter('commonFilter', (s) => ({
        ...s,
        serialNumber: '',
      }));
      if (activeCompanyId && activeCompanyId !== '0') {
        companyActions.getPlantsAppSync(activeCompanyId).then(() => {
          if (activePlantId && activePlantId !== '0') {
            performSiteChange(activePlantId);
          }
        });
      }
    },
    [companyActions, performSiteChange]
  );



  return (
    <>
      <DialogWithBackdropClick
        disableBackdropClick
        fullWidth={true}
        open={open}
        maxWidth={maxWidth || "sm"}
      >
        <StyledDialogTitle id="about-dialog-title">
          <div>{_T("Site Selection")}</div>
        </StyledDialogTitle>
        <DialogContent dividers>
          <RootContainer container spacing={1}>
            {!isExternalUser && <FormControlBox
              variant="outlined"
              size="small"
            >
              <Box sx={{ paddingBottom: '10px', width: '100%' }}>
                <PlantSearch
                  plantSearchTerm={appFilters.commonFilter.plantSearchTerm}
                  setPlantSearchTerm={setPlantSearchTerm}
                  onPlantSearchSelectionChanged={
                    plantSearchPlantSelectionChangedHandler
                  }
                />
              </Box>
            </FormControlBox>}
            <SiteSelectionComponent applyExtraSmallSize={true} />
          </RootContainer>
        </DialogContent>
        <ActionButtonsContainer>
          <Button id="close-btn" variant="contained" onClick={onCloseHandler}>
            {_T("Close")}
          </Button>
        </ActionButtonsContainer>
      </DialogWithBackdropClick>
    </>
  );
};

export default SiteSelectionDialog;
